import { defineStore } from 'pinia'
import api, { getUploadSession } from '../api'
import { useAuthStore } from './auth'
import Str from 'laravel-js-str'
import { DateTime } from 'luxon'
import { useNotificationStore } from './notifications'
import * as Sentry from '@sentry/vue'

export const useSystemStore = defineStore('system', {
  state: () => ({
    hub: null,
    routes: null,
    error: null,
    enums: null,
    enumMappings: null,
    activeUpload: null,
    darkMode: false,
    reducedMotion: true,
    confirmExitMethods: [],
    previousRoute: null,
    callToActions: [],
    reloadCallToActionsTimer: null,
    titleOverride: null,
    introOverride: null
  }),
  getters: {
    systemInitialized() {
      return this.hub !== null && this.routes !== null && this.enums !== null
    }
  },
  actions: {
    async initSystem() {
      try {
        const response = await api.get('/v3/members-hubs/discover')
        this.hub = response.data

        Sentry.setTag('hub_id', this.hub.id)
        Sentry.setTag('hub_name', this.hub.name)

        this.updateStyles()
      } catch (e) {
        if (e.response && e.response.status === 404) {
          this.error = 'Not found'
        }
      }
    },
    updateStyles() {
      const steps = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950]
      const base = `--color-base:${this.hub.colour_base};`
      const baseDark = this.hub.colour_base_dark ? `--color-base-dark:${this.hub.colour_base_dark};` : ''
      const accent = this.hub.colour_accent.map((hex, i) => `--color-accent-${steps[i]}:${hex};`).join('')
      let accentText = '#FFFFFF'
      if (this.hub.colour_accent_text === 'text-black') {
        accentText = '#000000'
      } else if (this.hub.colour_accent_text === 'text-base') {
        accentText = this.hub.colour_base
      }
      let fonts = [this.hub.font_heading, this.hub.font_body]
      fonts = fonts
        .filter((font, i) => fonts.indexOf(font) === i && !['Georgia'].includes(font))
        .map((font) => `family=${encodeURIComponent(font)}:wght@400;600;700`)
        .join('&')

      let googleFonts = document.getElementById('googlefonts') || document.createElement('link')
      googleFonts.id = 'googlefonts'
      googleFonts.setAttribute('rel', 'stylesheet')
      googleFonts.setAttribute('href', `https://fonts.googleapis.com/css2?${fonts}&display=swap`)
      document.head.appendChild(googleFonts)

      let cssVariables = document.getElementById('theme') || document.createElement('style')
      cssVariables.id = 'theme'
      const roundedCorners = this.hub.rounded_corners ? '' : '*:not(.rounded-full){border-radius:0 !important};'
      cssVariables.innerHTML = `:root{${base}${baseDark}${accent}--color-accent-text:${accentText};--font-heading:'${this.hub.font_heading}';--font-body:'${this.hub.font_body}';${roundedCorners}}`
      document.head.appendChild(cssVariables)
    },
    toggleDarkMode(value = null) {
      this.darkMode = value !== null ? value : !this.darkMode

      document.documentElement.classList.toggle('dark', this.darkMode)
    },
    toggleUiAnimations(value = null) {
      this.reducedMotion = value !== null ? value : !this.reducedMotion

      document.documentElement.classList.toggle('animation-disabled', this.reducedMotion)
    },
    async bootSystem() {
      const authStore = useAuthStore()
      const notificationStore = useNotificationStore()
      notificationStore.initLaravelEcho()
      await Promise.all([
        this.getRoutes(),
        this.getEnums(),
        authStore.getOffices(),
        notificationStore.getNotifications(),
        notificationStore.getDownloads()
      ])
    },
    async getRoutes(isCms = false) {
      const response = await api.get(`/v3/members-hubs/${this.hub.id}/routes`, {
        params: {
          is_cms: isCms
        }
      })
      this.routes = response.data
      return this.routes
    },
    async getEnums() {
      const response = await api.get(`/v3/utils/enums`)
      this.enums = response.data.enums
      this.enumMappings = response.data.mappings
    },
    rebuildRoutes(router, pageView) {
      let routesToKeep = []
      let tabPages = []

      // Rebuild the routes from the database
      for (let i = 0; i < this.routes.length; i++) {
        if (this.routes[i].is_external) {
          continue
        }

        const parentPage = this.routes.find((r) => r.id === this.routes[i].parent_id)
        const pageModel = this.routes[i].modules.find((m) => m.model !== null)
        if (parentPage && parentPage.show_tabs && !pageModel) {
          const name = `page${this.routes[i].id}`
          routesToKeep.push(name)
          tabPages.push({
            parent: `page${this.routes[i].parent_id}`,
            route: {
              name,
              path: this.routes[i].path,
              component: pageView,
              meta: this.routes[i]
            }
          })
          continue
        }

        let route = this.routes[i]
        let path = []
        do {
          let localPath = route.path
          const component = route.modules.find((m) => m.model !== null)
          if (component) {
            let key = component.model.split('\\')
            key = Str.camel(key[key.length - 1])
            if (Str.contains(path.join('/'), `:${key}(\\d+)/`)) {
              route = this.routes.find((r) => r.id === route.parent_id)
              continue
            }
            localPath = `:${key}(\\d+)/${localPath}`
          }
          path.unshift(localPath)
          route = this.routes.find((r) => r.id === route.parent_id)
        } while (route)
        path = path.join('/')

        const name = `page${this.routes[i].id}`
        routesToKeep.push(name)
        router.addRoute({
          name,
          path: Str.start(path, '/'),
          component: pageView,
          meta: this.routes[i]
        })
      }

      for (let i = 0; i < tabPages.length; i++) {
        router.addRoute(tabPages[i].parent, tabPages[i].route)
      }

      // Remove old routes
      let existingRoutes = router.getRoutes()
      for (let i = 0; i < existingRoutes.length; i++) {
        if (existingRoutes[i].name.indexOf('page') !== 0) {
          continue
        }

        if (routesToKeep.includes(existingRoutes[i].name)) {
          continue
        }

        router.removeRoute(existingRoutes[i].name)
      }

      router.replace(router.currentRoute.value.fullPath)
    },
    async getUploadSession() {
      if (this.uploadSession && DateTime.fromISO(this.uploadSession.expires) > DateTime.utc()) {
        return this.uploadSession
      }

      this.uploadSession = await getUploadSession()
      return this.uploadSession
    },
    registerConfirmExitMethod(method) {
      this.confirmExitMethods.push(method)
    },
    unregisterConfirmExitMethod(method) {
      const index = this.confirmExitMethods.indexOf(method)
      if (index >= 0) {
        this.confirmExitMethods.splice(index, 1)
      }
    },
    setPreviousRoute(route) {
      this.previousRoute = route
    },
    async getCallToActions() {
      clearTimeout(this.reloadCallToActionsTimer)
      const authStore = useAuthStore()

      const response = await api.get(`v3/members-hubs/${this.hub.id}/call-to-actions`, {
        params: {
          office_id: authStore.selectedOffice.id
        }
      })
      this.callToActions = response.data

      // Reload the CTA's every hour
      this.reloadCallToActionsTimer = setTimeout(this.getCallToActions, 1000 * 60 * 60)
    },
    overridePageTitle(title = null, intro = null) {
      this.titleOverride = title
      this.introOverride = intro

      if (title && this.hub) {
        document.title = title ? `${title} - ${this.hub?.name}` : this.hub?.name
      }
    }
  }
})
